import { useState, useMemo } from 'react';
import { useI18n } from '../../../../i18n';
import { es } from '../../../../i18n/es';
import { en } from '../../../../i18n/en';
import { zh } from '../../../../i18n/zh';

// ═══════════════════════════════════════════════════════════════
// AdminI18nTab — Translation keys overview (read-only)
// ═══════════════════════════════════════════════════════════════

interface FlatEntry {
  key: string;
  es: string;
  en: string;
  zh: string;
}

/** Flatten a nested object into dot-notation keys */
function flattenObj(obj: unknown, prefix = ''): Record<string, string> {
  const result: Record<string, string> = {};
  if (obj === null || obj === undefined) return result;
  if (typeof obj === 'string') {
    result[prefix] = obj;
    return result;
  }
  if (typeof obj !== 'object') return result;
  for (const [k, v] of Object.entries(obj as Record<string, unknown>)) {
    const newKey = prefix ? `${prefix}.${k}` : k;
    if (typeof v === 'string') {
      result[newKey] = v;
    } else if (typeof v === 'object' && v !== null && !Array.isArray(v)) {
      Object.assign(result, flattenObj(v, newKey));
    }
  }
  return result;
}

export function AdminI18nTab() {
  const { t } = useI18n();
  const [search, setSearch] = useState('');
  const [filterMissing, setFilterMissing] = useState(false);

  const entries = useMemo(() => {
    const flatEs = flattenObj(es);
    const flatEn = flattenObj(en);
    const flatZh = flattenObj(zh);

    const allKeys = new Set([
      ...Object.keys(flatEs),
      ...Object.keys(flatEn),
      ...Object.keys(flatZh),
    ]);

    const result: FlatEntry[] = [];
    for (const key of Array.from(allKeys).sort()) {
      result.push({
        key,
        es: flatEs[key] ?? '',
        en: flatEn[key] ?? '',
        zh: flatZh[key] ?? '',
      });
    }
    return result;
  }, []);

  const missingEn = entries.filter((e) => e.es && !e.en).length;
  const missingZh = entries.filter((e) => e.es && !e.zh).length;

  const filtered = useMemo(() => {
    let result = entries;

    if (filterMissing) {
      result = result.filter((e) => (e.es && !e.en) || (e.es && !e.zh));
    }

    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(
        (e) =>
          e.key.toLowerCase().includes(q) ||
          e.es.toLowerCase().includes(q) ||
          e.en.toLowerCase().includes(q) ||
          e.zh.toLowerCase().includes(q),
      );
    }

    return result;
  }, [entries, search, filterMissing]);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">{t.admin.i18n.titulo}</h2>
        <p className="text-sm text-gray-500">{t.admin.i18n.descripcion}</p>
      </div>

      {/* Stats */}
      <div className="flex flex-wrap gap-3">
        <span className="rounded-full bg-[#1F3864] px-3 py-1 text-xs font-medium text-white">
          {entries.length} {t.admin.i18n.clavesTotal}
        </span>
        <span
          className={`rounded-full px-3 py-1 text-xs font-medium ${missingEn > 0 ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'}`}
        >
          {missingEn} {t.admin.i18n.sinTraducirEN}
        </span>
        <span
          className={`rounded-full px-3 py-1 text-xs font-medium ${missingZh > 0 ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'}`}
        >
          {missingZh} {t.admin.i18n.sinTraducirZH}
        </span>
      </div>

      {/* Controls */}
      <div className="flex items-center gap-4">
        <input
          type="text"
          placeholder={t.admin.i18n.buscar}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="flex-1 rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-[#33BCE1] focus:outline-none focus:ring-1 focus:ring-[#33BCE1]"
        />
        <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer whitespace-nowrap">
          <input
            type="checkbox"
            checked={filterMissing}
            onChange={(e) => setFilterMissing(e.target.checked)}
            className="rounded border-gray-300"
          />
          {t.admin.i18n.soloSinTraducir}
        </label>
      </div>

      {/* Table */}
      <div className="overflow-auto rounded-lg border border-gray-200 max-h-[600px]">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-gray-50 z-10">
            <tr className="border-b border-gray-200">
              <th className="px-3 py-2 text-left font-semibold text-gray-700 w-[30%]">
                {t.admin.i18n.clave}
              </th>
              <th className="px-3 py-2 text-left font-semibold text-gray-700 w-[25%]">ES</th>
              <th className="px-3 py-2 text-left font-semibold text-gray-700 w-[25%]">EN</th>
              <th className="px-3 py-2 text-left font-semibold text-gray-700 w-[20%]">ZH</th>
            </tr>
          </thead>
          <tbody>
            {filtered.slice(0, 200).map((entry) => (
              <tr key={entry.key} className="border-b border-gray-100 hover:bg-gray-50">
                <td className="px-3 py-1.5 font-mono text-xs text-[#1F3864] break-all">
                  {entry.key}
                </td>
                <td className="px-3 py-1.5 text-xs text-gray-700 break-words">
                  {entry.es || <span className="text-gray-300">—</span>}
                </td>
                <td className="px-3 py-1.5 text-xs break-words">
                  {entry.en ? (
                    <span className="text-gray-700">{entry.en}</span>
                  ) : (
                    <span className="rounded bg-amber-50 px-1.5 py-0.5 text-amber-600 font-medium">
                      {t.admin.i18n.falta}
                    </span>
                  )}
                </td>
                <td className="px-3 py-1.5 text-xs break-words">
                  {entry.zh ? (
                    <span className="text-gray-700">{entry.zh}</span>
                  ) : (
                    <span className="rounded bg-amber-50 px-1.5 py-0.5 text-amber-600 font-medium">
                      {t.admin.i18n.falta}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length > 200 && (
          <p className="px-3 py-2 text-xs text-gray-400 text-center">
            {t.admin.i18n.mostrando} 200 / {filtered.length}
          </p>
        )}
        {filtered.length === 0 && (
          <p className="px-3 py-6 text-sm text-gray-400 text-center">
            {t.admin.i18n.sinResultados}
          </p>
        )}
      </div>
    </div>
  );
}
